import { USE_EXTERNAL_API } from '@m/config/env';
import { apiFetch, apiFetchV2 } from '@m/utils/http';
import { trackUserLoggedIn } from 'apps/customer/utils/heapOnboardTracking';

import type { User } from '@m/types/api';
import type { OnboardingFlow, ProfileFieldSource } from '@m/types/grpc/MemberOnboardingSource';

const UserService = {
  getUser: async () => {
    const response = await apiFetch('/user');

    if (response.success) {
      trackUserLoggedIn(!!response.data?.user?.isRegistered);
    } else {
      trackUserLoggedIn(false);
    }

    return response;
  },

  logout: () => apiFetch('/logout', { method: 'POST' }),

  getUserSession: () => apiFetch('/user/visit/state'),

  parseInvitationCode: ({ invitationCode }: { invitationCode?: string }) =>
    apiFetch('/user/invitation', {
      params: {
        invitationCode,
      },
    }),

  parseMemberReferralInvitationCode: ({ invitationCode }: { invitationCode?: string }) =>
    apiFetch('/member/redeem-invitation', {
      params: {
        secret: invitationCode,
      },
    }),

  signUpWithInvitation: ({
    invitationCode,
    firstName,
    lastName,
    email,
    password,
  }: {
    email?: string;
    firstName?: string;
    invitationCode?: string;
    lastName?: string;
    password?: string;
  }) =>
    apiFetch('/user/invitation', {
      method: 'POST',
      params: { invitationCode },
      body: {
        firstName,
        lastName,
        email,
        password,
      },
    }),

  // No invitation code is required
  signUpRegister: ({
    firstName,
    lastName,
    email,
    password,
    phoneNumber,
    agreements,
    onboardingFlow,
    profileFieldSource,
  }: {
    agreements?: { id: number; type: { id: number } }[];
    email?: string;
    firstName?: string;
    lastName?: string;
    onboardingFlow?: OnboardingFlow;
    password?: string;
    phoneNumber?: string;
    profileFieldSource?: ProfileFieldSource;
  }) =>
    apiFetch('/register', {
      method: 'POST',
      body: {
        provider: 'metropolis',
        firstName,
        lastName,
        email,
        password,
        phoneNumber,
        agreements,
        onboardingFlow,
        profileFieldSource,
      },
    }),

  /**
   * Registers an unregistered customer
   *
   * This route accepts latest agreements and updates the user registration status
   */
  registerCustomerAndAcceptTerms: ({
    provider,
    onboardingFlow,
    profileFieldSource,
  }: {
    onboardingFlow?: OnboardingFlow;
    profileFieldSource?: ProfileFieldSource;
    provider: 'metropolis'; // only metropolis for now
  }) =>
    apiFetch('/v2/register', {
      method: 'POST',
      body: {
        provider,
        onboardingFlow,
        profileFieldSource,
      },
    }),

  // Sends verification code if user exists or doesn't exist
  requestCode: ({ phoneNumber }: { phoneNumber: string }) =>
    apiFetch('/user/verification/send', {
      method: 'POST',
      body: {
        phoneNumber,
      },
    }),

  verifyCode: ({
    phoneNumber,
    code: secret,
    beginSession, // Stores session in cookie, else receives session token
  }: {
    beginSession: boolean;
    code: string;
    phoneNumber?: string | null;
  }) => {
    const sessionMode = beginSession ? 'cookie' : undefined;
    return apiFetch('/user/verification', {
      params: {
        phoneNumber,
        secret,
        sessionMode: USE_EXTERNAL_API ? 'token' : sessionMode,
      },
    });
  },

  // Verifies the OTP code and auto-registers the user in one step (express onboarding).
  // POST /user/verification/register — added by EN-9585
  verifyAndRegister: ({
    phoneNumber,
    code: secret,
    beginSession,
    onboardingFlow,
    profileFieldSource,
  }: {
    beginSession: boolean;
    code: string;
    onboardingFlow?: OnboardingFlow;
    phoneNumber?: string | null;
    profileFieldSource?: ProfileFieldSource;
  }) => {
    const sessionMode = beginSession ? 'cookie' : undefined;
    return apiFetchV2<{ token?: string; user: User }>('/user/verification/register', {
      method: 'POST',
      params: {
        phoneNumber,
        secret,
        sessionMode: USE_EXTERNAL_API ? 'token' : sessionMode,
      },
      body: {
        onboardingFlow,
        profileFieldSource,
      },
    });
  },

  updateUserInfo: ({
    phoneNumber,
    email,
    firstName,
    lastName,
  }: {
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    phoneNumber?: string | null;
  }) =>
    apiFetch('/customer/update-info', {
      method: 'PUT',
      body: {
        phoneNumber,
        email,
        firstName,
        lastName,
      },
    }),

  getActiveSubscriptions: () => apiFetch('/customer/subscription'),

  deleteAccount: (userId: number) =>
    apiFetch(`/customer/${userId}`, {
      method: 'DELETE',
    }),

  requestUpdateVerificationCode: ({ phoneNumber }: { phoneNumber: string }) =>
    apiFetch('/user/verification/update-phone/send', {
      method: 'POST',
      body: {
        phoneNumber,
      },
    }),

  verifyUpdateCode: ({ phoneNumber, secret }: { phoneNumber: string | null; secret: string }) =>
    apiFetch('/user/verify-phone-update', {
      method: 'POST',
      body: {
        phoneNumber,
        secret,
      },
    }),
};

export default UserService;
